/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

const toggleVariants = cva(
  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tutara-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-gray-200 dark:bg-gray-700',
        success: 'bg-gray-200 dark:bg-gray-700',
      },
      checked: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        checked: true,
        className: 'bg-tutara-primary',
      },
      {
        variant: 'success',
        checked: true,
        className: 'bg-tutara-primary',
      },
    ],
    defaultVariants: {
      variant: 'default',
      checked: false,
    },
  }
);

const thumbVariants = cva(
  'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform',
  {
    variants: {
      checked: {
        true: 'translate-x-5',
        false: 'translate-x-0',
      },
    },
    defaultVariants: {
      checked: false,
    },
  }
);

export interface TutaraToggleProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'>,
    Omit<VariantProps<typeof toggleVariants>, 'checked'> {
  label?: string;
  description?: string;
  onCheckedChange?: (checked: boolean) => void;
}

const TutaraToggle = React.forwardRef<HTMLInputElement, TutaraToggleProps>(
  (
    {
      className,
      variant,
      label,
      description,
      checked,
      disabled,
      onCheckedChange,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    // Fall back to a generated id so the (sr-only) input and its visible
    // <label htmlFor> stay associated even when no id/name is passed — without
    // it, clicking the toggle does nothing.
    const generatedId = React.useId();
    const toggleId = id || props.name || generatedId;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;
      onChange?.(e);
      onCheckedChange?.(newChecked);
    };

    return (
      <div className="flex items-start gap-3">
        <div className="relative inline-flex">
          <input
            ref={ref}
            type="checkbox"
            id={toggleId}
            className="sr-only peer"
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            {...props}
          />
          <label
            htmlFor={toggleId}
            className={cn(
              toggleVariants({
                variant,
                checked: !!checked,
              }),
              className
            )}
          >
            <span className={thumbVariants({ checked: !!checked })} />
          </label>
        </div>
        {(label || description) && (
          <div className="flex-1">
            {label && (
              <label
                htmlFor={toggleId}
                className="block text-sm font-medium text-tutara-charcoal dark:text-white cursor-pointer"
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-sm text-tutara-charcoal/70 dark:text-gray-400 mt-0.5">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

TutaraToggle.displayName = 'TutaraToggle';

export { TutaraToggle, toggleVariants };
